// ============================================
// Patient Journey Engine (PJE) Firestore Service
//
// Persistence for personas, test runs, generated
// panels, and load sim results. Admin-only tool.
//
// Collections:
//   pje_personas/{personaId}
//   pje_test_runs/{runId}
//   pje_panels/{panelId}
//   pje_load_runs/{runId}
// ============================================

import {
  collection, doc, getDoc, getDocs, setDoc, deleteDoc,
  query, where, orderBy, limit as firestoreLimit, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { TestRun, GeneratedPanel, LoadSimRun, ModifierFlow, RegisterEvaluation, NurseTraitProfile } from '../../types/patientJourney';
import type { PersonaData } from '../../components/admin/patient-journey/PersonaCard';
import { HEMOLOGIX_ENV_VALUE } from '../patientJourney/twinService';

import {
  C_PJE_LOAD_RUNS,
  C_PJE_MODIFIER_FLOWS,
  C_PJE_PANELS,
  C_PJE_PERSONAS,
  C_PJE_REGISTER_EVALUATIONS,
  C_PJE_TEST_RUNS,
  C_PJE_TRAIT_PROFILES,
} from '../../constants/collections';
// ── Collection refs ──

const personasCol = () => collection(db, C_PJE_PERSONAS);
const testRunsCol = () => collection(db, C_PJE_TEST_RUNS);
const panelsCol = () => collection(db, C_PJE_PANELS);
const loadRunsCol = () => collection(db, C_PJE_LOAD_RUNS);
const modifierFlowsCol = () => collection(db, C_PJE_MODIFIER_FLOWS);
const registerEvalsCol = () => collection(db, C_PJE_REGISTER_EVALUATIONS);
const traitProfilesCol = () => collection(db, C_PJE_TRAIT_PROFILES);

// ── Extended persona type with vertical for Firestore ──

export interface PJEPersonaDoc extends PersonaData {
  vertical?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Test run filters ──

export interface TestRunFilters {
  personaId?: string;
  touchpointType?: string;
  status?: string;
  suiteId?: string;
  limit?: number;
}

// ── Aggregated stats ──

export interface TestStats {
  totalRuns: number;
  passRate: number;
  lastRunAt: string | null;
  byPersona: Record<string, { runs: number; passRate: number }>;
  byTouchpoint: Record<string, { runs: number; passRate: number }>;
}

// ==========================================================
// PERSONAS
// ==========================================================

/** Save a custom persona to Firestore. Returns the docId. */
export async function savePersona(
  persona: PersonaData,
  vertical?: string,
): Promise<string> {
  const id = persona.personaId;
  const now = new Date().toISOString();

  const docData: PJEPersonaDoc = {
    ...persona,
    vertical: vertical ?? undefined,
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(doc(personasCol(), id), {
    ...(stripUndefined(docData) as Record<string, unknown>),
    _updatedAt: Timestamp.now(),
  });

  return id;
}

/** Get all saved personas. */
export async function getPersonas(): Promise<PJEPersonaDoc[]> {
  const snap = await getDocs(query(personasCol(), orderBy('_updatedAt', 'desc')));
  return snap.docs.map((d) => d.data() as PJEPersonaDoc);
}

/** Get personas filtered by vertical. */
export async function getPersonasByVertical(vertical: string): Promise<PJEPersonaDoc[]> {
  const snap = await getDocs(
    query(personasCol(), where('vertical', '==', vertical), orderBy('_updatedAt', 'desc')),
  );
  return snap.docs.map((d) => d.data() as PJEPersonaDoc);
}

/** Get a single persona by ID. */
export async function getPersona(personaId: string): Promise<PJEPersonaDoc | null> {
  const snap = await getDoc(doc(personasCol(), personaId));
  return snap.exists() ? (snap.data() as PJEPersonaDoc) : null;
}

/** Delete a persona. */
export async function deletePersona(personaId: string): Promise<void> {
  await deleteDoc(doc(personasCol(), personaId));
}

// ==========================================================
// TEST RUNS
// ==========================================================

/** Save a test run result. Returns the runId. */
export async function saveTestRun(run: TestRun): Promise<string> {
  const id = run.runId;

  await setDoc(doc(testRunsCol(), id), {
    ...(stripUndefined(run) as Record<string, unknown>),
    // Test data isolation (Section 2.3) — tag every PJE run so queries
    // can distinguish test data from future production data.
    environment: HEMOLOGIX_ENV_VALUE,
    _createdAt: Timestamp.now(),
  });

  return id;
}

/** Get test runs with optional filters. */
export async function getTestRuns(filters?: TestRunFilters): Promise<TestRun[]> {
  const constraints: Parameters<typeof query>[1][] = [];

  if (filters?.personaId) {
    constraints.push(where('personaId', '==', filters.personaId));
  }
  if (filters?.touchpointType) {
    constraints.push(where('touchpointId', '==', filters.touchpointType));
  }
  if (filters?.status) {
    constraints.push(where('status', '==', filters.status));
  }
  if (filters?.suiteId) {
    constraints.push(where('suiteId', '==', filters.suiteId));
  }

  constraints.push(orderBy('_createdAt', 'desc'));

  if (filters?.limit) {
    constraints.push(firestoreLimit(filters.limit));
  }

  // Build query with spread constraints
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const q = query(testRunsCol(), ...constraints as any[]);
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as TestRun);
}

/** Get a single test run by ID. */
export async function getTestRun(runId: string): Promise<TestRun | null> {
  const snap = await getDoc(doc(testRunsCol(), runId));
  return snap.exists() ? (snap.data() as TestRun) : null;
}

/** Get aggregated stats across all test runs. */
export async function getTestStats(): Promise<TestStats> {
  const snap = await getDocs(query(testRunsCol(), orderBy('_createdAt', 'desc')));
  const runs = snap.docs.map((d) => d.data() as TestRun);

  const totalRuns = runs.length;

  if (totalRuns === 0) {
    return {
      totalRuns: 0,
      passRate: 0,
      lastRunAt: null,
      byPersona: {},
      byTouchpoint: {},
    };
  }

  // Overall pass rate
  const totalPassRate = runs.reduce((sum, r) => sum + (r.passRate ?? 0), 0) / totalRuns;

  // Last run timestamp
  const lastRunAt = runs[0]?.startedAt ?? null;

  // By persona
  const byPersona: Record<string, { runs: number; passRate: number }> = {};
  for (const r of runs) {
    const key = r.personaId;
    if (!byPersona[key]) byPersona[key] = { runs: 0, passRate: 0 };
    byPersona[key].runs += 1;
    byPersona[key].passRate += r.passRate ?? 0;
  }
  for (const key of Object.keys(byPersona)) {
    byPersona[key].passRate = byPersona[key].passRate / byPersona[key].runs;
  }

  // By touchpoint
  const byTouchpoint: Record<string, { runs: number; passRate: number }> = {};
  for (const r of runs) {
    const key = r.touchpointId;
    if (!byTouchpoint[key]) byTouchpoint[key] = { runs: 0, passRate: 0 };
    byTouchpoint[key].runs += 1;
    byTouchpoint[key].passRate += r.passRate ?? 0;
  }
  for (const key of Object.keys(byTouchpoint)) {
    byTouchpoint[key].passRate = byTouchpoint[key].passRate / byTouchpoint[key].runs;
  }

  return {
    totalRuns,
    passRate: totalPassRate,
    lastRunAt,
    byPersona,
    byTouchpoint,
  };
}

// ==========================================================
// GENERATED PANELS
// ==========================================================

/** Save a generated panel. Returns the panelId. */
export async function savePanel(panel: GeneratedPanel): Promise<string> {
  const id = panel.panelId;

  await setDoc(doc(panelsCol(), id), {
    ...(stripUndefined(panel) as Record<string, unknown>),
    _createdAt: Timestamp.now(),
  });

  return id;
}

/** Get recent panels, optionally limited. */
export async function getPanels(max: number = 50): Promise<GeneratedPanel[]> {
  const snap = await getDocs(
    query(panelsCol(), orderBy('_createdAt', 'desc'), firestoreLimit(max)),
  );
  return snap.docs.map((d) => d.data() as GeneratedPanel);
}

/** Get a single panel by ID. */
export async function getPanel(panelId: string): Promise<GeneratedPanel | null> {
  const snap = await getDoc(doc(panelsCol(), panelId));
  return snap.exists() ? (snap.data() as GeneratedPanel) : null;
}

// ==========================================================
// LOAD SIM RUNS (future — scaffold)
// ==========================================================

/** Save a load simulation run. Returns the runId. */
export async function saveLoadRun(run: LoadSimRun): Promise<string> {
  const id = run.runId;

  await setDoc(doc(loadRunsCol(), id), {
    ...(stripUndefined(run) as Record<string, unknown>),
    _createdAt: Timestamp.now(),
  });

  return id;
}

/** Get recent load sim runs. */
export async function getLoadRuns(max: number = 20): Promise<LoadSimRun[]> {
  const snap = await getDocs(
    query(loadRunsCol(), orderBy('_createdAt', 'desc'), firestoreLimit(max)),
  );
  return snap.docs.map((d) => d.data() as LoadSimRun);
}

/** Get a single load run by ID. */
export async function getLoadRun(runId: string): Promise<LoadSimRun | null> {
  const snap = await getDoc(doc(loadRunsCol(), runId));
  return snap.exists() ? (snap.data() as LoadSimRun) : null;
}

// ==========================================================
// MODIFIER FLOWS (v3.1)
// ==========================================================

/** Save a modifier flow record. */
export async function saveModifierFlow(flow: ModifierFlow): Promise<string> {
  const id = flow.flowId;
  await setDoc(doc(modifierFlowsCol(), id), {
    ...(stripUndefined(flow) as Record<string, unknown>),
    _createdAt: Timestamp.now(),
  });
  return id;
}

/** Get modifier flows for a given run. */
export async function getModifierFlows(runId: string): Promise<ModifierFlow[]> {
  const snap = await getDocs(
    query(modifierFlowsCol(), where('runId', '==', runId), orderBy('_createdAt', 'desc')),
  );
  return snap.docs.map((d) => d.data() as ModifierFlow);
}

/** Get a single modifier flow by ID. */
export async function getModifierFlow(flowId: string): Promise<ModifierFlow | null> {
  const snap = await getDoc(doc(modifierFlowsCol(), flowId));
  return snap.exists() ? (snap.data() as ModifierFlow) : null;
}

// ==========================================================
// REGISTER EVALUATIONS (v3.1)
// ==========================================================

/** Save a register evaluation. */
export async function saveRegisterEvaluation(evaluation: RegisterEvaluation): Promise<string> {
  const id = evaluation.evalId;
  await setDoc(doc(registerEvalsCol(), id), {
    ...(stripUndefined(evaluation) as Record<string, unknown>),
    _createdAt: Timestamp.now(),
  });
  return id;
}

/** Get register evaluations for a given run. */
export async function getRegisterEvaluations(runId: string): Promise<RegisterEvaluation[]> {
  const snap = await getDocs(
    query(registerEvalsCol(), where('runId', '==', runId), orderBy('_createdAt', 'desc')),
  );
  return snap.docs.map((d) => d.data() as RegisterEvaluation);
}

/** Get a single register evaluation by ID. */
export async function getRegisterEvaluation(evalId: string): Promise<RegisterEvaluation | null> {
  const snap = await getDoc(doc(registerEvalsCol(), evalId));
  return snap.exists() ? (snap.data() as RegisterEvaluation) : null;
}

// ==========================================================
// TRAIT PROFILES (Nurse Tuning Presets)
// ==========================================================

export interface SavedTraitProfile {
  profileId: string;
  name: string;
  description?: string;
  traitProfile: NurseTraitProfile;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Save a trait profile preset. Returns profileId. */
export async function saveTraitProfile(profile: SavedTraitProfile): Promise<string> {
  const id = profile.profileId;
  await setDoc(doc(traitProfilesCol(), id), {
    ...(stripUndefined(profile) as Record<string, unknown>),
    _updatedAt: Timestamp.now(),
  });
  return id;
}

/** Get all saved trait profiles, newest first. */
export async function getTraitProfiles(): Promise<SavedTraitProfile[]> {
  const snap = await getDocs(query(traitProfilesCol(), orderBy('_updatedAt', 'desc')));
  return snap.docs.map((d) => d.data() as SavedTraitProfile);
}

/** Get a single trait profile by ID. */
export async function getTraitProfile(profileId: string): Promise<SavedTraitProfile | null> {
  const snap = await getDoc(doc(traitProfilesCol(), profileId));
  return snap.exists() ? (snap.data() as SavedTraitProfile) : null;
}

/** Delete a trait profile. */
export async function deleteTraitProfile(profileId: string): Promise<void> {
  await deleteDoc(doc(traitProfilesCol(), profileId));
}

// ==========================================================
// HELPERS
// ==========================================================

/** Strip undefined values from an object (Firestore rejects undefined). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}
